import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../../../layouts/DashboardLayout';
import CadastralSearch from '../../components/CadastralSearch/CadastralSearch';
import CadastralMap from '../../components/CadastralMap/CadastralMap';
import CadastralRecordDrawer from '../../components/CadastralRecordDrawer/CadastralRecordDrawer';
import {
  validateCadastralKey,
  findLocalProperty
} from '../../services/cadastral.service';
import { cadastralApi } from '../../api/cadastral.api';
import { QUERY_STEPS } from '../../data/catalogs.constants';
import { PROPERTY_REGISTRY } from '../../data/cadastral-registry.data';
import styles from '../../styles/Cadastral.module.css';

const sleep = ms => new Promise(r => setTimeout(r, ms));

export default function CadastralPage() {
  const location = useLocation();
  const [currentProperty, setCurrentProperty] = useState(null);
  const [isDetail, setIsDetail] = useState(false);

  // Search and query flow state.
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progressPct, setProgressPct] = useState(0);
  const [searchStatus, setSearchStatus] = useState(null); // null | 'ok' | 'not_found' | 'invalid_format' | 'no_geo'
  const [statusInfo, setStatusInfo] = useState(null);

  const isConsultingRef = useRef(false);

  /* Simulate web service query steps. */
  const runQuerySteps = useCallback(async (stepLimit = QUERY_STEPS.length) => {
    for (let i = 0; i < stepLimit; i++) {
      setCurrentStep(i);
      setProgressPct(Math.round(((i + 0.5) / QUERY_STEPS.length) * 100));
      await sleep(180 + Math.random() * 120);
      setProgressPct(Math.round(((i + 1) / QUERY_STEPS.length) * 100));
    }
  }, []);

  /* Main query handler. */
  const handleSearch = useCallback(async (rawKey) => {
    if (isConsultingRef.current) return;

    // Preliminary validation.
    const validationResult = validateCadastralKey(rawKey);
    if (!validationResult.valido) {
      setSearchStatus('invalid_format');
      setStatusInfo({
        titulo: validationResult.titulo,
        mensaje: validationResult.mensaje
      });
      setCurrentProperty(null);
      setIsDetail(false);
      return;
    }

    const recordKey = validationResult.clave;
    isConsultingRef.current = true;
    setIsLoading(true);
    setCurrentStep(0);
    setProgressPct(5);
    setSearchStatus(null);
    setStatusInfo(null);
    setIsDetail(false);

    // Run the step animation and microservice request concurrently.
    // This keeps the progress bar moving while the request is in flight.
    const apiCall = cadastralApi.getPropertyRecord(recordKey).catch(() => '__API_FAIL__');
    const [apiRaw] = await Promise.all([apiCall, runQuerySteps(QUERY_STEPS.length)]);

    // If the API fails or returns null, try the local fallback.
    let result = null;
    if (apiRaw && apiRaw !== '__API_FAIL__') {
      result = apiRaw;
    } else {
      result = findLocalProperty(recordKey);
    }

    if (!result) {
      // The key was not found in the API or local data.
      await sleep(200);
      setIsLoading(false);
      setSearchStatus('not_found');
      setStatusInfo({ clave: recordKey });
      setCurrentProperty(null);
      toast.error(`Sin registros para la clave ${recordKey}`);
      isConsultingRef.current = false;
      return;
    }

    await sleep(150);

    const { record } = result;
    setCurrentProperty(result);
    setIsLoading(false);

    if (record.latitude && record.longitude) {
      setSearchStatus('ok');
      setStatusInfo({
        clave: record.propertyKey,
        direccion: record.address,
        colonia: record.neighborhood
      });
      toast.success('Predio localizado en el mapa');
    } else {
      setSearchStatus('no_geo');
      setStatusInfo({ clave: record.propertyKey });
      toast('El predio no cuenta con georreferencia', { icon: '⚠️' });
    }

    isConsultingRef.current = false;
  }, [runQuerySteps]);

  /* Open the property record. */
  const handleOpenRecord = useCallback(() => {
    if (currentProperty) {
      setIsDetail(true);
      setSearchStatus(null); // Clear the search message.
    }
  }, [currentProperty]);

  /* Return to the map. */
  const handleReturnToMap = useCallback(() => {
    setIsDetail(false);
  }, []);

  /* Support an initial query through the URL (#consulta=... or ?clave=...). */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keyFromUrl = params.get('clave') || params.get('c');
    if (keyFromUrl) {
      handleSearch(keyFromUrl);
      return;
    }

    const hash = window.location.hash || '';
    const queryMatch = /#consulta(?:=(\d+))?/.exec(hash);
    if (queryMatch && queryMatch[1]) {
      handleSearch(queryMatch[1]);
    }
  }, [location.search, handleSearch]);

  return (
    <DashboardLayout noPadding>
      <div className={`${styles.geo} ${isDetail ? styles.isDetail : ''}`} id="geo">
        {/* Collapsible property record viewer. */}
        <aside
          className={styles.recordPanel}
          id="recordPanel"
          aria-label="Expediente catastral"
          aria-hidden={!isDetail}
        >
          <CadastralRecordDrawer
            data={currentProperty}
            onReturnToMap={handleReturnToMap}
          />
        </aside>

        {/* Main map section. */}
        <div className={styles.geoMap}>
          {/* Floating Google Maps-style search. */}
          <CadastralSearch
            onSearch={handleSearch}
            onOpenRecord={handleOpenRecord}
            isLoading={isLoading}
            currentStep={currentStep}
            progressPct={progressPct}
            searchStatus={searchStatus}
            statusInfo={statusInfo}
            totalRecords={PROPERTY_REGISTRY.length}
          />

          {/* Map with Leaflet or fallback. */}
          <CadastralMap
            currentProperty={currentProperty}
            isDetail={isDetail}
            onOpenRecord={handleOpenRecord}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
